import { useEffect, useRef } from 'react';

import { normalizedPolyToBBox } from 'shared/utils/geometry';

import {
  findScrollableAncestor,
  resolveRevealScrollTop,
} from './page-highlight.helpers';

/**
 * Bounding-box overlay for `@react-pdf-viewer` pages (SA-1812).
 *
 * Shared by both PDF renderers in this codebase — `shared/ui/pdf-viewer`
 * (create-claim panels) and `document-viewer/pdf-viewer-organism` (claim detail)
 * — so a highlight looks and behaves identically wherever a document opens.
 */

/**
 * One highlight rectangle drawn over a rendered page.
 *
 * `pageWidth` / `pageHeight` come from `renderPage`, which reports the page's
 * already-scaled and already-rotated size — so multiplying the normalized
 * polygon by them makes the box follow zoom and rotation with no extra math.
 *
 * Jumping to the right page is not enough on its own: a box near the bottom of a
 * tall page lands below the fold, so the user sees a page with no visible marking
 * and has to hunt for it. The box scrolls itself into view on mount and whenever
 * its geometry changes (another finding on the same page, a zoom change).
 */
function PageHighlightBox({
  normalizedVertices,
  pageWidth,
  pageHeight,
  revealToken,
}) {
  const boxRef = useRef(null);
  const box = normalizedPolyToBBox(normalizedVertices, pageWidth, pageHeight);
  const isMeasurable =
    Number.isFinite(box.x) &&
    Number.isFinite(box.y) &&
    box.width > 0 &&
    box.height > 0;

  // Depends on the box's own primitives, not the object (rebuilt every render).
  // `revealToken` is what makes re-picking the SAME finding scroll again: the
  // coordinates are identical, so geometry alone would never re-trigger this.
  useEffect(() => {
    const element = boxRef.current;
    if (!element) return;

    const container = findScrollableAncestor(element);
    // jsdom and older engines have no `scrollTo` on elements — the highlight
    // still renders, it just does not self-reveal.
    if (typeof container?.scrollTo !== 'function') return;

    const boxRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const top = resolveRevealScrollTop({
      boxTop: boxRect.top,
      boxHeight: boxRect.height,
      viewTop: containerRect.top,
      viewHeight: container.clientHeight,
      scrollTop: container.scrollTop,
    });
    if (top === null) return;

    container.scrollTo({ top, behavior: 'smooth' });
  }, [box.x, box.y, box.width, box.height, revealToken]);

  if (!isMeasurable) return null;

  return (
    <div
      ref={boxRef}
      aria-hidden="true"
      className="pointer-events-none absolute rounded-sm border-2 border-error-600 bg-error-600/10"
      style={{
        left: box.x,
        top: box.y,
        width: box.width,
        height: box.height,
      }}
    />
  );
}

/** A page + polygon pair is only drawable when both are present and sane. */
export const hasDrawableHighlight = (highlightPage, highlightVertices) =>
  Number.isInteger(highlightPage) &&
  highlightPage >= 1 &&
  Array.isArray(highlightVertices) &&
  highlightVertices.length >= 3;

/**
 * Build a `renderPage` for `<Viewer>` that keeps the stock layers and adds the
 * box on one page.
 *
 * `highlightPage` is 1-based (it comes straight from a BE page number) while
 * `renderPage` reports a 0-based `pageIndex` — the offset is applied here so no
 * caller has to remember it.
 */
export const buildHighlightRenderer =
  ({ highlightPage, highlightVertices, revealToken }) =>
  (props) => (
    <>
      {props.canvasLayer.children}
      {props.textLayer.children}
      {props.annotationLayer.children}
      {props.pageIndex === highlightPage - 1 ? (
        <PageHighlightBox
          normalizedVertices={highlightVertices}
          pageWidth={props.width}
          pageHeight={props.height}
          revealToken={revealToken}
        />
      ) : null}
    </>
  );
